import { Link } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import clsx from 'clsx';
import Icon from './Icon';
import type { AuctionListItem } from '../stores/auction';
import { AUCTION_CATEGORIES } from '@auction/shared';

interface AuctionCardProps {
  auction: AuctionListItem;
}

const getCategoryLabel = (value: string) => {
  return AUCTION_CATEGORIES.find(c => c.value === value)?.label || 'Other';
};

export default function AuctionCard({ auction }: AuctionCardProps) {
  const statusColors: Record<string, string> = {
    active: 'badge-active',
    ending: 'badge-ending',
    closed: 'badge-closed',
    sold: 'badge-sold',
    scheduled: 'bg-blue-100 text-blue-800',
    unsold: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-500',
  };

  const endTime = new Date(auction.endTime);
  const isEnding = auction.status === 'ending' || (auction.status === 'active' && endTime.getTime() - Date.now() < 5 * 60 * 1000);

  // List API provides currentPrice and bidCount directly
  const displayPrice = auction.currentPrice;
  const bidCount = auction.bidCount;

  return (
    <Link to={`/auction/${auction.id}`} className="card hover:shadow-lg transition-shadow">
      <div className="aspect-video bg-gray-200 relative">
        {auction.imageUrl ? (
          <img
            src={auction.imageUrl}
            alt={auction.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon name="image" className="text-gray-300 text-6xl" />
          </div>
        )}
        <div className="absolute top-2 left-2">
          <span className="badge bg-white/90 text-gray-700 text-xs">
            {getCategoryLabel(auction.category)}
          </span>
        </div>
        <div className="absolute top-2 right-2">
          <span className={clsx('badge', statusColors[auction.status])}>
            {auction.status}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-lg truncate">{auction.title}</h3>
        <p className="text-sm text-gray-500 mt-1">by {auction.sellerName}</p>
        
        <div className="mt-3 flex items-baseline justify-between">
          <div>
            <p className="text-sm text-gray-500">Current bid</p>
            <p className="text-xl font-bold text-primary-600">
              £{(displayPrice / 100).toFixed(2)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">
              {['active', 'ending'].includes(auction.status) ? 'Ends in' : 'Ended'}
            </p>
            <p className={clsx('text-sm font-medium', isEnding && 'text-red-600 animate-pulse')}>
              {['active', 'ending'].includes(auction.status)
                ? formatDistanceToNow(endTime)
                : format(endTime, 'MMM d, yyyy')}
            </p>
          </div>
        </div>

        {bidCount > 0 && (
          <p className="mt-2 text-sm text-gray-500">
            {bidCount} bid{bidCount !== 1 ? 's' : ''}
          </p>
        )}
      </div>
    </Link>
  );
}
